import React, { useState } from "react";

import { UNSUPPORTED_UHC_SIC_CODES, UNSUPPORTED_DISABILITY_SIC_CODES } from "EmployerApplication/Components/Constants";
import { OSHA_SIC_CODES } from "EmployerApplication/Components/SICCodes";
import { getCompanyApplication } from "EmployerApplication/Selectors";
import { uniq } from "lodash";
import { useForm } from "react-final-form";
import { useSelector } from "react-redux";
import Select, { components } from "react-select";
import AnalyticsTracking from "Utils/AnalyticsTracking";

import { BodyMedium } from "Cobalt/BodyText";
import { Callout } from "Cobalt/Callout";
import AppearingElement from "Common/AppearingElement";
import BodyText from "Common/BodyText";
import { Div } from "Common/HTML";

import makeBem from "Utils/makeBem";

const bem = makeBem("SICCodeSelectSearch");

const SICCodeSelectSearch = ({ shouldPrefillSICCode, isDisability }) => {
  const highlightMatchingTerm = (text, term) => {
    if (!term) {
      return text;
    }
    const parts = text.split(new RegExp(`(${term.replace(/([()])/g, "")})`, "gi"));
    // remove parenthesis from term to avoid regex errors
    return (
      <span>
        {parts.map((part, i) => (
          <span key={i} style={part.toLowerCase() === term.toLowerCase() ? { fontWeight: "bold" } : {}}>
            {part}
          </span>
        ))}
      </span>
    );
  };

  const Option = (props) => {
    const userInput = props?.selectProps?.inputValue || "";
    const option = props.data;

    // fix lag issue, replaced with css
    const { onMouseMove, onMouseOver, ...rest } = props.innerProps;
    const newProps = { ...props, innerProps: rest };

    return (
      <components.Option {...newProps} className={bem("customOption")}>
        <Div pb={2}>
          <BodyMedium weight="bold" py={2}>
            {highlightMatchingTerm(option.sic_name, userInput)} ({highlightMatchingTerm(option.sic_code, userInput)})
          </BodyMedium>
          <BodyText className={bem("description")}>{highlightMatchingTerm(option.sic_description, userInput)}</BodyText>
          {option.sic_examples && (
            <Div py={3}>
              <BodyText className={bem("examples")}>
                <u>Examples</u>
                {": "}
                <em>{highlightMatchingTerm(option.sic_examples, userInput)}</em>
              </BodyText>
            </Div>
          )}
        </Div>
      </components.Option>
    );
  };

  const NoOptionsMessage = (props) => (
    <components.NoOptionsMessage {...props}>
      <BodyText>
        We weren’t able to find any matches, please change your keywords to be more generic (e.g. if you make Legos type in “toy” and select the closest match
        from the options available)
      </BodyText>
    </components.NoOptionsMessage>
  );

  const unsupportSICCodes = isDisability ? UNSUPPORTED_DISABILITY_SIC_CODES : [...UNSUPPORTED_UHC_SIC_CODES, "8999"];

  const isUnsupportedSICCode = (sicCode) => unsupportSICCodes.includes(sicCode);

  const companyApplication = getCompanyApplication(useSelector((state) => state));

  const companySICCode = companyApplication?.sicCode;
  const SICCodeDetails = OSHA_SIC_CODES.find((sicCode) => sicCode.sic_code === companySICCode);

  const [selectedOption, setSelectedOption] = useState(shouldPrefillSICCode ? SICCodeDetails || null : null);

  const location = isDisability ? "disability" : "life";

  const recordSelectedOption = (option) => {
    if (option) {
      AnalyticsTracking.trackEvent(`${location}_SIC_filled`, {
        selection: option,
      });
    }
    if (isUnsupportedSICCode(option?.sic_code)) {
      AnalyticsTracking.trackEvent(`${location}_SIC_denied`, {
        selection: option,
      });
    }
  };

  const formatLabel = (option) => `${option.sic_name} (${option.sic_code})`;

  const [inputValue, setInputValue] = useState(selectedOption ? formatLabel(selectedOption) : "");

  const form = useForm();

  const customResults = (option, rawInput = "") => {
    const { sic_code: sicCode, sic_name: sicName, sic_description: sicDescription, sic_examples: sicExamples } = option.data;
    const input = rawInput.toLowerCase();

    if (selectedOption) return true; // if a sic code is already selected, show all results
    if ([1, 2].includes(rawInput.length)) return false; // helps performance by not searching until 3 characters are entered but hides no results message on initial load

    if (
      sicCode.toLowerCase().includes(input) ||
      sicExamples.toLowerCase().includes(input) ||
      sicName.toLowerCase().includes(input) ||
      sicDescription.toLowerCase().includes(input) ||
      formatLabel(option.data).toLowerCase().includes(input)
    ) {
      return true;
    }
    return false;
  };

  const searchPrioritized = (options) => {
    const input = inputValue.toLowerCase();
    const filteredByCode = options.filter((option) => option.sic_code.toLowerCase().includes(input));

    const filteredByName = options.filter((option) => option.sic_name.toLowerCase().includes(input));

    const filteredByExamples = options.filter((option) => option.sic_examples.toLowerCase().includes(input));

    const filteredByDescription = options.filter((option) => option.sic_description.toLowerCase().includes(input));

    const prioritizedResults = uniq([...filteredByCode, ...filteredByName, ...filteredByExamples, ...filteredByDescription]);

    return prioritizedResults;
  };

  const [options, setOptions] = useState(OSHA_SIC_CODES);
  const _setInputValue = (inputValue) => {
    setInputValue(inputValue);
    setOptions(searchPrioritized(OSHA_SIC_CODES));
  };

  return (
    <div>
      <Select
        className="basic-single"
        classNamePrefix={bem("select")}
        isClearable
        isSearchable
        menuShouldScrollIntoView
        captureMenuScroll
        backspaceRemovesValue
        blurInputOnSelect
        onKeyDown={(e) => {
          if (e.key === "Backspace") setSelectedOption(null);
        }}
        placeholder={`e.g. "general contractor", "ice cream shop"`}
        name="company_application[sic_code]"
        options={options}
        getOptionLabel={(option) => formatLabel(option)}
        getOptionValue={(option) => `${option.sic_code}`}
        components={{ Option, NoOptionsMessage }}
        value={selectedOption}
        onChange={(value, { action }) => {
          form.change("company_application[sic_code]", value?.sic_code);
          recordSelectedOption(value);
          setSelectedOption(value);
          if (action === "clear") {
            _setInputValue("");
          }
        }}
        inputValue={inputValue}
        onInputChange={_setInputValue}
        filterOption={customResults}
        inputId="sic_code_select_search"
      />
      <AppearingElement show={isUnsupportedSICCode(selectedOption?.sic_code)}>
        <Callout
          type="warning"
          className="my-2 w-8/12 md:w-full max-w-full"
          body={
            <BodyText py={2} ml={1}>
              Unfortunately, we are not able to offer {location} insurance for this business type at this time.
            </BodyText>
          }
        />
      </AppearingElement>
    </div>
  );
};

export default SICCodeSelectSearch;
